/**
 * Die Rahmenverträge und die Preise darin.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „die Rahmenvertrag aufbohren auf die Rahmenvertragsnummer, kommt mit über
 *    das LEB über die E-Mail. Da kommen dann noch die Preise rein, damit wir
 *    eine Trennung haben, welcher Auftrag dann zukünftig über welchen
 *    Rahmenvertrag reinkommt und dann entsprechend auch die richtige
 *    Bepreisung haben."
 *
 * ER STEHT IN DEN STAMMDATEN, NEBEN DEM LEISTUNGSKATALOG, und zwar aus
 * demselben Grund, aus dem der Katalog dort steht: es ist die Liste, gegen
 * die abgerechnet wird. Ein eigener Menüpunkt für zwei Verträge wäre einer zu
 * viel - dieselbe Lehre wie bei den Pollercodes.
 *
 * DIE PREISE SIND GELD und hängen an einem eigenen Recht. Wer es nicht hat,
 * bekommt sie GAR NICHT ERST GESCHICKT: eine Anzeige, die etwas verbirgt, hat
 * es trotzdem im Speicher.
 */

import { useEffect, useState } from 'react';
import { api, datum, heuteIso} from '../api.ts';
import {
  Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage,
} from './Formular.tsx';
import { useFrisch } from './Frisch.tsx';

/** Euro, deutsch. Vier Nachkommastellen nur, wenn es welche gibt. */
function euro(w: unknown): string {
  if (w === null || w === undefined || w === '') return '';
  const n = Number(w);
  if (!Number.isFinite(n)) return '';
  const s = n.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: Number.isInteger(n * 100) ? 2 : 4,
  });
  return `${s} €`;
}

export function Rahmenvertraege() {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [bearbeite, setBearbeite] = useState<any | null>(null);
  const [preise, setPreise] = useState<string | null>(null);

  const laden = () => api.rahmenvertraege().then(setD)
    .catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  if (!d) {
    return (
      <div className="karte">
        <Fehlerkasten lage={fehler} />
        <p className="leise klein">wird geladen …</p>
      </div>
    );
  }

  return (
    <>
      <Fehlerkasten lage={fehler} />

      <div className="karte">
        <h3>Rahmenverträge</h3>
        <div className="leib">
          <p className="klein leise">
            Jeder Auftrag wird unter einem Rahmenvertrag der Telekom
            abgerufen. Die Nummer steht auf dem Abruf hinter „Abruf zu
            Kontrakt" und wird beim Einlesen des Sammeldrucks automatisch an
            den Auftrag geschrieben. Eine Nummer, die hier noch nicht steht,
            wird dabei angelegt und gemeldet - benennen und bepreisen tut sie
            der Innendienst.
          </p>

          {/* WIE VIELE AUFTRÄGE OHNE VERTRAG DASTEHEN, IST DIE ARBEITSLISTE
              DIESES BILDSCHIRMS. Ohne Vertrag gibt es keinen Preis, und ohne
              Preis fehlt die Zeile in jeder Summe der Auswertung. */}
          {d.ohne_vertrag > 0 && (
            <p className="hinweis warnung">
              {d.ohne_vertrag} Auftrag/Aufträge tragen keinen Rahmenvertrag.
              Sie bekommen ihn mit dem nächsten Sammeldruck; bis dahin fehlen
              sie in jeder Preisauswertung.
            </p>
          )}

          {d.darfPflegen && (
            <div className="reihe">
              <button className="knopf haupt"
                      onClick={() => setBearbeite({})}>
                Neuer Rahmenvertrag
              </button>
            </div>
          )}

          {!d.zeilen.length ? (
            <p className="leise klein">Es steht noch kein Rahmenvertrag da.</p>
          ) : (
            <div className="rollt">
              <table className="liste">
                <thead>
                  <tr>
                    <th>Nummer</th>
                    <th>Bezeichnung</th>
                    <th>Laufzeit</th>
                    <th className="zahl">Aufträge</th>
                    <th className="zahl" title="Positionen des Katalogs mit einem hinterlegten Preis">
                      Preise
                    </th>
                    <th className="knopfleiste breit" />
                  </tr>
                </thead>
                <tbody>
                  {d.zeilen.map((v: any) => (
                    <tr key={v.id} className={v.aktiv ? undefined : 'leise'}>
                      <td><code>{v.nummer}</code></td>
                      <td>
                        {v.bezeichnung ?? <span className="leise">ohne Namen</span>}
                        {!v.aktiv && <span className="marke"> außer Kraft</span>}
                      </td>
                      <td className="klein">
                        {v.gueltig_von || v.gueltig_bis
                          ? `${datum(v.gueltig_von) || '…'} – ${datum(v.gueltig_bis) || '…'}`
                          : <span className="leise">unbefristet</span>}
                      </td>
                      <td className="zahl einfarbig">{v.auftraege}</td>
                      <td className="zahl einfarbig"
                          title={`${v.positionen_ohne_preis} von `
                               + `${v.positionen_im_katalog} Positionen haben `
                               + 'noch keinen Preis'}>
                        {v.positionen_mit_preis} / {v.positionen_im_katalog}
                        {v.positionen_ohne_preis > 0 && (
                          <span className="warnfarbe"> *</span>
                        )}
                      </td>
                      <td className="knopfleiste breit">
                        {d.darfPreise && (
                          <button className="knopf"
                                  onClick={() => setPreise(v.id)}>
                            Preise
                          </button>
                        )}
                        {d.darfPflegen && (
                          <button className="knopf"
                                  onClick={() => setBearbeite(v)}>
                            ändern
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!d.darfPreise && (
            <p className="klein leise">
              Die Preise sind Geld und hängen am Recht „Preise je
              Rahmenvertrag". Es liegt bei der Administration.
            </p>
          )}
        </div>
      </div>

      {bearbeite && (
        <VertragFenster v={bearbeite}
                        zu={() => setBearbeite(null)}
                        fertig={() => { setBearbeite(null); laden(); }} />
      )}
      {preise && (
        <PreisFenster id={preise}
                      zu={() => { setPreise(null); laden(); }} />
      )}
    </>
  );
}

/** Einen Rahmenvertrag anlegen oder ändern. */
function VertragFenster({
  v, zu, fertig,
}: { v: any; zu: () => void; fertig: () => void }) {
  const [w, setW] = useState<any>({
    id: v.id, nummer: v.nummer ?? '', bezeichnung: v.bezeichnung ?? '',
    gueltig_von: v.gueltig_von ?? '', gueltig_bis: v.gueltig_bis ?? '',
    aktiv: v.aktiv === undefined ? true : !!v.aktiv,
    bemerkung: v.bemerkung ?? '',
  });
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  async function speichern() {
    setLaeuft(true); setFehler(null);
    try { await api.rahmenvertragSpeichern(w); fertig(); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  return (
    <Dialog titel={v.id ? 'Rahmenvertrag ändern' : 'Neuer Rahmenvertrag'}
            untertitel={v.nummer ?? undefined}
            laeuft={laeuft} zu={zu} breite={720}>
      <Fehlerkasten lage={fehler} />
      <div className="reihe">
        <Text name="nummer" titel="Rahmenvertragsnummer" wert={w.nummer}
              setze={(x: string) => setW({ ...w, nummer: x })} />
        <Text name="bezeichnung" titel="Bezeichnung" wert={w.bezeichnung}
              setze={(x: string) => setW({ ...w, bezeichnung: x })} />
      </div>
      <p className="klein leise">
        Sie steht auf dem Abruf hinter „Abruf zu Kontrakt", zum Beispiel
        4650017416.
      </p>
      <div className="reihe">
        <span className="feldpaar">
          <label className="klein leise" htmlFor="rv-von">gültig von</label>
          <input id="rv-von" type="date" value={w.gueltig_von ?? ''}
                 onChange={(e) => setW({ ...w, gueltig_von: e.target.value })} />
        </span>
        <span className="feldpaar">
          <label className="klein leise" htmlFor="rv-bis">gültig bis</label>
          <input id="rv-bis" type="date" value={w.gueltig_bis ?? ''}
                 onChange={(e) => setW({ ...w, gueltig_bis: e.target.value })} />
        </span>
        <label className="feldpaar">
          <input type="checkbox" checked={!!w.aktiv}
                 onChange={(e) => setW({ ...w, aktiv: e.target.checked })} />
          {' '}in Kraft
        </label>
      </div>
      <Text name="bemerkung" titel="Bemerkung" wert={w.bemerkung} mehrzeilig
            setze={(x: string) => setW({ ...w, bemerkung: x })} />
      {/* EIN VERTRAG WIRD NIE GELÖSCHT, SONDERN AUSSER KRAFT GESETZT. An ihm
          hängen Aufträge und Preise; was einmal abgerechnet wurde, muss
          erklärbar bleiben - dieselbe Regel wie beim Leistungskatalog. */}
      <p className="klein leise">
        Ein Rahmenvertrag wird nicht gelöscht, sondern außer Kraft gesetzt. An
        ihm hängen Aufträge und Preise; was einmal abgerechnet wurde, muss
        erklärbar bleiben.
      </p>
      <div className="reihe">
        <button className="knopf haupt" disabled={laeuft} onClick={speichern}>
          Speichern
        </button>
        <button className="knopf" onClick={zu}>Abbrechen</button>
      </div>
    </Dialog>
  );
}

/**
 * Die Preise eines Vertrags, je Position des Katalogs.
 *
 * ES STEHT DER GANZE KATALOG DA, auch was keinen Preis hat: „welche fehlen"
 * ist die Frage, mit der man dieses Fenster aufmacht.
 */
function PreisFenster({ id, zu }: { id: string; zu: () => void }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});
  const [ab, setAb] = useState(() => heuteIso());
  const [nurOffene, setNurOffene] = useState(false);

  const laden = () => api.rahmenvertragPreise(id).then((a) => {
    setD(a); setEntwurf({});
  }).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, [id]);
  useFrisch(laden);

  async function setzen(zeile: any) {
    const wert = entwurf[zeile.lv_position_id];
    if (wert === undefined || wert === '') return;
    setLaeuft(true); setFehler(null);
    try {
      await api.rahmenvertragPreisSetzen(id, {
        lv_position_id: zeile.lv_position_id,
        einheitspreis: wert,
        gueltig_ab: ab,
      });
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  async function weg(zeile: any) {
    if (!zeile.preis_id) return;
    setLaeuft(true); setFehler(null);
    try { await api.rahmenvertragPreisWeg(id, zeile.preis_id); await laden(); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  const zeilen = (d?.zeilen ?? []).filter(
    (z: any) => !nurOffene || z.einheitspreis === null
                           || z.einheitspreis === undefined);

  return (
    <Dialog titel="Preise je Position"
            untertitel={d
              ? `${d.vertrag.nummer}${d.vertrag.bezeichnung ? ', ' + d.vertrag.bezeichnung : ''}`
              : undefined}
            laeuft={laeuft} zu={zu} breite={1040}>
      <Fehlerkasten lage={fehler} />
      {!d ? (
        <p className="leise klein">wird geladen …</p>
      ) : (
        <>
          {/* EIN PREIS GILT AB EINEM TAG. Eine Anpassung ersetzt den alten
              Wert nicht, sie stellt einen neuen daneben - sonst rechnet die
              Auswertung des letzten Jahres mit den Preisen von heute, und die
              Verhandlungsgrundlage ist falsch, ohne dass es auffällt. */}
          <div className="reihe">
            <span className="feldpaar">
              <label className="klein leise" htmlFor="preis-ab">gültig ab</label>
              <input id="preis-ab" type="date" value={ab}
                     onChange={(e) => setAb(e.target.value)} />
            </span>
            <label className="feldpaar">
              <input type="checkbox" checked={nurOffene}
                     onChange={(e) => setNurOffene(e.target.checked)} />
              {' '}nur Positionen ohne Preis
            </label>
          </div>
          <p className="klein leise">
            Ein Preis gilt AB einem Tag. Eine Anpassung ersetzt den alten Wert
            nicht, sie stellt einen neuen daneben und beendet den vorigen am
            Vortag. Sonst rechnet die Auswertung des letzten Jahres mit den
            Preisen von heute.
          </p>

          <div className="rollt">
            <table className="liste">
              <thead>
                <tr>
                  <th>OZ</th>
                  <th>Position</th>
                  <th>Einheit</th>
                  <th className="zahl">Preis heute</th>
                  <th>gilt ab</th>
                  <th className="zahl">neuer Preis</th>
                  <th className="knopfleiste breit" />
                </tr>
              </thead>
              <tbody>
                {zeilen.map((z: any) => (
                  <tr key={z.lv_position_id}>
                    <td><code>{z.oz}</code></td>
                    <td>{z.text}</td>
                    <td className="klein leise">{z.einheit}</td>
                    <td className="zahl einfarbig">
                      {z.oz === '10099980'
                        /* DIE GEBÜHRENPOSITION TRÄGT DEN EUROBETRAG ALS
                           MENGE (Migration 033). Ein Einheitspreis daran wäre
                           Euro mal Euro - das Feld bleibt deshalb zu, und der
                           Grund steht daneben. */
                        ? <span className="leise">Menge = Euro</span>
                        : z.einheitspreis === null || z.einheitspreis === undefined
                          ? <span className="warnfarbe">kein Preis</span>
                          : euro(z.einheitspreis)}
                    </td>
                    <td className="klein leise">{datum(z.gueltig_ab)}</td>
                    <td className="zahl">
                      {z.oz !== '10099980' && (
                        <input type="text" inputMode="decimal"
                               style={{ width: '7em', textAlign: 'right' }}
                               value={entwurf[z.lv_position_id] ?? ''}
                               placeholder="0,00"
                               aria-label={`Neuer Preis für ${z.oz}`}
                               onChange={(e) => setEntwurf({
                                 ...entwurf,
                                 [z.lv_position_id]: e.target.value,
                               })} />
                      )}
                    </td>
                    <td className="knopfleiste breit">
                      {z.oz !== '10099980' && (
                        <button className="knopf" disabled={laeuft
                                || !entwurf[z.lv_position_id]}
                                onClick={() => setzen(z)}>
                          setzen
                        </button>
                      )}
                      {z.preis_id && (
                        <button className="knopf gefahr" disabled={laeuft}
                                onClick={() => weg(z)}
                                title="Nur zum Zurücknehmen eines Vertippers. Zum Beenden eines Preises trägt man den Nachfolger ein.">
                          weg
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {d.verlauf.length > 0 && (
            <details>
              <summary>Frühere Preise ({d.verlauf.length})</summary>
              <div className="rollt">
                <table className="liste">
                  <thead>
                    <tr>
                      <th>OZ</th>
                      <th className="zahl">Preis</th>
                      <th>von</th>
                      <th>bis</th>
                      <th>Bemerkung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.verlauf.map((p: any) => (
                      <tr key={p.id}>
                        <td><code>{p.oz}</code></td>
                        <td className="zahl einfarbig">{euro(p.einheitspreis)}</td>
                        <td className="klein">{datum(p.gueltig_ab)}</td>
                        <td className="klein">
                          {p.gueltig_bis
                            ? datum(p.gueltig_bis)
                            : <span className="leise">läuft</span>}
                        </td>
                        <td className="klein leise">{p.bemerkung ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          <p className="klein leise">
            Der Preis hängt am PAAR aus Vertrag und Position und nicht an der
            Position selbst. Deshalb bleiben der Leistungskatalog, das Aufmaß
            und der LEB-Export preislos: der Preis dient der Auswertung und der
            nächsten Verhandlung und geht in keine Datei, die das Haus
            verlässt.
          </p>
        </>
      )}
      <div className="reihe">
        <button className="knopf haupt" onClick={zu}>Fertig</button>
      </div>
    </Dialog>
  );
}
